import { Config } from '@mainstage/config';
import { IdentityApi } from '@mainstage/core-plugin-api';
import _ from 'lodash';
import hashObject from 'object-hash';
import { Dispatch, SetStateAction } from 'react';
import { AutocompleteOption, FetchOptionsResponse } from './schema';
import { ApiSpec, fetchMicroservice } from '../utils/microserviceProxy';

const isUsingOldOffsetBehaviour = (config: Config | undefined) => {
  return Boolean(
    config?.getOptionalBoolean(
      'mesh.builder.ui.remoteProvider.oldOffsetBehaviour',
    ),
  );
};

/**
 * This function enables to extract dynamic fields, i.e. fields that are identified by some special characters "${{ }}".
 * It extracts the values that are inside the above special characters and resolve them by using the formContext.
 * @param formContext
 * @param params
 * @returns
 */
export function parseDynamicFields(
  formContext: any,
  prevFormContext: any,
  resetValues: (value: string[]) => void,
  setEnableSearch: (value: boolean) => void,
  setOffset: (value: number) => void,
  setMessages?: Dispatch<
    SetStateAction<
      Map<string, { line: string; severity: 'info' | 'warning' | 'error' }>
    >
  >,
  params?: any,
): any | undefined {
  const dynamicFieldChecker = /^\${1}\{{2}\s*[a-zA-Z0-9_\-]+\s*\}{2}$/g;

  const dynamicFieldExtractor = /[\$\{\}\s]/g;

  if (Array.isArray(params)) {
    return params
      .map(value =>
        parseDynamicFields(
          formContext,
          prevFormContext,
          resetValues,
          setEnableSearch,
          setOffset,
          setMessages,
          value,
        ),
      )
      .filter(val => val !== undefined);
  }

  if (typeof params === 'string' && dynamicFieldChecker.test(params)) {
    const fieldName = params.replaceAll(dynamicFieldExtractor, '');
    if (fieldName) {
      const prevFieldNameValue = _.get(prevFormContext, fieldName);
      const currFieldNameValue = _.get(formContext, fieldName);
      if (!_.isEqual(prevFieldNameValue, currFieldNameValue)) {
        setOffset(0);
        setEnableSearch(true);
        resetValues([]);
      }

      if (setMessages) {
        if (!currFieldNameValue) {
          setMessages(prevErrors =>
            prevErrors.set(fieldName, {
              line: `There is a dependency to the field "${fieldName}". Remember to fill it before going to the next step.`,
              severity: 'warning',
            }),
          );
        } else {
          setMessages(prevErrors => {
            prevErrors.delete(fieldName);
            return prevErrors;
          });
        }
      }

      return currFieldNameValue;
    }

    return params;
  }

  if (typeof params === 'object') {
    return Object.entries(params)
      .map(([field, value]) => [
        field,
        parseDynamicFields(
          formContext,
          prevFormContext,
          resetValues,
          setEnableSearch,
          setOffset,
          setMessages,
          value,
        ),
      ])
      .reduce(
        (accumulator, [field, value]) => ({ ...accumulator, [field]: value }),
        {},
      );
  }

  return params;
}

/**
 * This method validates the input UI schema for the Custom URL Picker
 * @param uiSchema
 * @returns
 */
export function validateUiSchema(uiSchema: any) {
  if (!uiSchema['ui:apiSpec']) {
    throw new Error(
      "'ui:apiSpec' property must be defined. See the docs if you want additional info.",
    );
  }

  if (!uiSchema['ui:apiSpec'].retrieval) {
    throw new Error(
      "'retrieval' property should be configured under 'ui:apiSpec' property. See the docs if you want additional info.",
    );
  }

  if (!uiSchema['ui:options']?.selectedField) {
    throw new Error(
      "'selectedField' should be defined under 'ui:options' property.",
    );
  }

  if (!uiSchema['ui:displayFields']) {
    throw new Error(
      "'api:displayFields' property must be defined for CustomUrlPicker. See the docs if you want additional info.",
    );
  }

  if (
    !uiSchema['ui:apiSpec'].validation &&
    uiSchema['ui:apiSpec'].retrieval.baseUrl !== '{{CURRENT_HOST}}'
  ) {
    throw new Error(
      `'ui:apiSpec.validation' property must be defined for CustomUrlPicker. See the docs if you want additional info.`,
    );
  }

  if (uiSchema['ui:fieldsToSave']) {
    const intersection = uiSchema['ui:displayFields'].filter((field: string) =>
      uiSchema['ui:fieldsToSave'].includes(field),
    );

    if (intersection.length !== uiSchema['ui:displayFields'].length) {
      throw new Error(
        `'ui:displayFields' is not a sub-set of 'ui:fieldsToSave'. This may cause some problems in the visualization of the results in the drop-down list.`,
      );
    }
  }

  if (
    !uiSchema['ui:displayFields'].includes(
      uiSchema['ui:options']?.selectedField,
    )
  ) {
    throw new Error(
      `'ui:options'.selectedField is not included in the 'ui:displayFields' parameter. Please, choose as 'selectedField' a field that is contained also in the 'displayFields' parameter.`,
    );
  }

  return uiSchema;
}

/**
 * It performs an HTTP call to a Microservice which specifications are made available in the "apiSpec" parameter
 * and by passing "params" in the body of the request. It returns a list of objects filtered by "filter" corresponding to the offset "offset".
 * @param apiSpec: the api specification from the template (ui:apiSpec)
 * @param page the current "page" of results to fetch, it is used to compute the offset
 * @param params it contains the body of request and also the "limit" parameter to pass to the request
 * @param filter a string used to filter results
 * @returns
 */
export async function fetchMicroserviceValues(
  apiSpec: ApiSpec,
  page: number,
  params?: Record<string, any>,
  filter?: string,
  config?: Config | undefined,
  identityApi?: IdentityApi,
): Promise<FetchOptionsResponse> {
  const oldOffsetBehaviour = isUsingOldOffsetBehaviour(config);

  const { limit, ...restParams } = params ?? {};

  const backendUrl = config?.getOptionalString('backend.baseUrl') || '';

  const credentials = await identityApi?.getCredentials();
  const token = credentials?.token;

  const { ok, data } = await fetchMicroservice({
    backendBaseUrl: backendUrl,
    operation: 'retrieval',
    apiSpec,
    page,
    limit,
    oldOffsetBehaviour,
    filter,
    body: restParams,
    token,
  });

  if (!ok) {
    return {
      ok: false,
      options: [],
    };
  }

  return {
    ok: true,
    options: data,
  };
}

/**
 * It performs multiple HTTP calls to a Microservice which specifications are made available in the "apiSpec" parameter
 * and by passing "params" in the body of the request.
 * The requests are made with an incremental offset and a predetermined limit(it overrides the ones in the params if present), until a response with 0 elements is met (it means all values are fetched)
 * @param apiSpec: the api specification from the template (ui:apiSpec)
 * @param params it contains the body of request and also the "limit" parameter to pass to the request
 * @returns
 */
export async function fetchAllMicroserviceValues(
  apiSpec: ApiSpec,
  params?: any,
  config?: Config | undefined,
  identityApi?: IdentityApi,
): Promise<AutocompleteOption[]> {
  const total: AutocompleteOption[] = [];
  const LIMIT = 1000;
  let currentPage = 0;
  let fetchMore = true;
  while (fetchMore) {
    const response = await fetchMicroserviceValues(
      apiSpec,
      currentPage,
      { ...params, limit: LIMIT },
      undefined,
      config,
      identityApi,
    );
    currentPage++;
    if (response.ok) total.push(...response.options);
    if (!response.options.length) {
      fetchMore = false;
    }
  }
  // Remove duplicates based on the "id" field
  const unique = _.uniqBy(total, 'id');
  return unique;
}

export function getApiConfigId(
  apiSpec: ApiSpec,
  params?: Record<string, any> & { limit?: number },
) {
  const filteredParams = _.cloneDeep(params ?? {});
  // we don't want limit to affect the hash, we will fetch all the options anyway
  if (filteredParams.limit) delete filteredParams.limit;
  const microserviceId =
    apiSpec.retrieval?.microserviceId ??
    `${apiSpec.retrieval?.baseUrl}/${apiSpec.retrieval?.path}`;
  return hashObject({ params: filteredParams, microserviceId });
}
